// src/utils/dispatchHandoverPdf.js
// A4 "Dispatched Orders" courier handover sheet — spec: docs/DISPATCHED_ORDERS_PDF_FORMAT.md.
import autoTable from 'jspdf-autotable';
import { createDoc, urlToDataUrl } from './offlineOrderPdfExporter';

export const FIRST_PAGE_ROWS_PER_COLUMN = 39;
export const ROWS_PER_COLUMN = 71;

const pad = (n) => String(n).padStart(2, '0');

export const formatStamp = (d = new Date(), dateSep = '-', timeSep = ':', joiner = ' ') =>
  `${d.getFullYear()}${dateSep}${pad(d.getMonth() + 1)}${dateSep}${pad(d.getDate())}${joiner}${pad(d.getHours())}${timeSep}${pad(d.getMinutes())}${timeSep}${pad(d.getSeconds())}`;

export const formatCod = (amount) =>
  Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const HANDOVER_TOP_Y = 8;
const HANDOVER_ROW_H = 4;
const LOGO_SIZE = 18;

// Fixed row height so each column can be filled exactly down to the footer (mm, A4).
export const getHandoverRowsPerColumn = (headerBottomY, pageH = 297) => {
  const bottomY = pageH - 9;
  return {
    firstPerCol: Math.max(1, Math.floor((bottomY - headerBottomY) / HANDOVER_ROW_H) - 1),
    restPerCol: Math.floor((bottomY - HANDOVER_TOP_Y) / HANDOVER_ROW_H) - 1,
  };
};

// Mirrors the header y-advances in downloadDispatchHandoverPdf so the on-screen preview paginates identically.
export const getHandoverHeaderBottomY = (logoRatio = null, confirmLineCount = 1) => {
  const logoH = logoRatio ? (logoRatio >= 1 ? LOGO_SIZE / logoRatio : LOGO_SIZE) + 4 : 0;
  return HANDOVER_TOP_Y + logoH + 12 + 7 + 9 + (confirmLineCount * 4.5 + 3) + 9 + 6.5 + 6.5 + 9 + 6.5 + 6.5 + 7 + 3;
};

// Splits rows into pages; each page fills its left column before its right one, numbering continuous.
export const paginateHandoverRows = (rows = [], firstPerCol = FIRST_PAGE_ROWS_PER_COLUMN, perColRest = ROWS_PER_COLUMN) => {
  const numbered = rows.map((r, i) => ({ ...r, n: i + 1 }));
  const pages = [];
  let start = 0;
  while (start < numbered.length || pages.length === 0) {
    const perCol = pages.length === 0 ? firstPerCol : perColRest;
    const slice = numbered.slice(start, start + perCol * 2);
    pages.push({ left: slice.slice(0, perCol), right: slice.slice(perCol) });
    start += perCol * 2;
  }
  return pages;
};

export const getHandoverTitleDate = (data) =>
  data?.metadata?.dispatchedDate || formatStamp(new Date(data?.metadata?.generatedAt || Date.now())).split(' ')[0];

// Re-encodes any browser-decodable image (webp, svg, jpeg...) to PNG so jsPDF can embed it.
const toPngDataUrl = (dataUrl) => new Promise((resolve) => {
  if (!dataUrl) return resolve(null);
  const img = new Image();
  img.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve({ dataUrl: canvas.toDataURL('image/png'), ratio: img.naturalWidth / img.naturalHeight || 1 });
    } catch {
      resolve(null);
    }
  };
  img.onerror = () => resolve(null);
  img.src = dataUrl;
});

const BLACK = [0, 0, 0];
const HEAD_FILL = [240, 240, 240];
const GREY = [85, 85, 85];

const labelValue = (doc, x, y, label, value) => {
  doc.setFont('helvetica', 'bold');
  doc.text(label, x, y);
  const w = doc.getTextWidth(label + ' ');
  doc.setFont('helvetica', 'normal');
  doc.text(value, x + w, y);
  return x + w + doc.getTextWidth(value);
};

const buildDoc = async ({ data, businessName, logoUrl }) => {
  const { doc, pageW, pageH } = createDoc();
  const ml = 6;
  const gap = 4;
  const colW = (pageW - ml * 2 - gap) / 2;
  const rows = data.handoverRows || [];
  const totalCod = data.totalCod ?? rows.reduce((s, r) => s + Number(r.cod || 0), 0);
  doc.setTextColor(...BLACK);

  let y = 8;
  const logo = await toPngDataUrl(await urlToDataUrl(logoUrl));
  if (logo) {
    try {
      const size = LOGO_SIZE;
      const w = logo.ratio >= 1 ? size : size * logo.ratio;
      const h = logo.ratio >= 1 ? size / logo.ratio : size;
      doc.addImage(logo.dataUrl, 'PNG', pageW / 2 - w / 2, y, w, h);
      y += h + 4;
    } catch { /* render without logo */ }
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12.5);
  doc.text(`Dispatched Orders | ${getHandoverTitleDate(data)}`, pageW / 2, y + 4, { align: 'center' });
  y += 12;

  doc.setFontSize(9);
  const rightX = ml + colW + gap;
  labelValue(doc, ml, y, 'Company Name:', businessName || '');
  labelValue(doc, rightX, y, 'Courier Representative:', '__________________');
  y += 7;
  labelValue(doc, ml, y, 'Courier Company:', data.courierCompany?.name || '');
  const afterDate = labelValue(doc, rightX, y, 'Date:', '__________');
  labelValue(doc, afterDate + 6, y, 'Time:', '__________');
  y += 9;

  const sx = ml;
  doc.setFont('helvetica', 'normal');
  const confirmLines = doc.splitTextToSize('We confirm that the following parcels have been dispatched and handed over to the courier representative for delivery.', pageW - sx * 2);
  doc.text(confirmLines, sx, y);
  y += confirmLines.length * 4.5 + 3;
  const afterParcels = labelValue(doc, sx, y, 'Total Parcels:', String(rows.length));
  labelValue(doc, afterParcels + 10, y, 'Total COD Amount:', formatCod(totalCod));
  y += 9;
  doc.setFont('helvetica', 'bold');
  doc.text('Dispatch Confirmation', sx, y);
  y += 6.5;
  doc.setFont('helvetica', 'normal');
  doc.text('Name: ________________________________', sx, y);
  y += 6.5;
  doc.text('Signature: ___________________________', sx, y);
  y += 9;
  doc.setFont('helvetica', 'bold');
  doc.text('Parcel Collection Confirmation', sx, y);
  y += 6.5;
  doc.setFont('helvetica', 'normal');
  doc.text('Name (Courier): ________________________________', sx, y);
  y += 6.5;
  doc.text('Signature (Courier): ___________________________', sx, y);
  y += 7;
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY);
  doc.text(`Generated on ${formatStamp(new Date())}`, sx, y);
  doc.setTextColor(...BLACK);
  y += 3;

  const topY = HANDOVER_TOP_Y;
  const { firstPerCol, restPerCol } = getHandoverRowsPerColumn(y, pageH);

  const head = [['#', 'Waybill Id', 'Order No.', 'City', 'COD']];
  const pct = [0.07, 0.19, 0.3, 0.27, 0.17];
  const columnStyles = Object.fromEntries(pct.map((p, i) => [i, { cellWidth: colW * p }]));
  columnStyles[2].overflow = 'linebreak';
  columnStyles[4].halign = 'right';
  const toBody = (list) => list.map(r => [r.n, r.waybillId || '', r.orderNumber || '', r.city || '', formatCod(r.cod)]);

  paginateHandoverRows(rows, firstPerCol, restPerCol).forEach((page, pi) => {
    if (pi > 0) {
      doc.addPage();
      y = topY;
    }
    [page.left, page.right].forEach((list, side) => {
      if (!list.length && side === 1) return;
      autoTable(doc, {
        startY: y,
        head,
        body: toBody(list),
        margin: { left: side === 0 ? ml : rightX, right: 0, top: 6, bottom: 6 },
        tableWidth: colW,
        pageBreak: 'avoid',
        rowPageBreak: 'avoid',
        theme: 'grid',
        columnStyles,
        styles: {
          font: 'helvetica', fontSize: 7.2, textColor: BLACK, lineColor: BLACK, lineWidth: 0.2, minCellHeight: HANDOVER_ROW_H,
          cellPadding: { top: 0.4, bottom: 0.4, left: 0.8, right: 0.8 }, overflow: 'ellipsize', valign: 'middle',
        },
        headStyles: { fillColor: HEAD_FILL, textColor: BLACK, fontStyle: 'bold' },
      });
    });
  });

  const total = doc.getNumberOfPages();
  for (let i = 1; i <= total; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.text(`Page ${i} of ${total}`, pageW - 7, pageH - 6.3, { align: 'right' });
  }
  return doc;
};

export const downloadDispatchHandoverPdf = async (args) => {
  const doc = await buildDoc(args);
  doc.save(`dispatched-orders-${formatStamp(new Date(), '-', '-', '-')}.pdf`);
};
